import React, { useState, useCallback, useRef, useEffect } from 'react';
import type { IAfterGuiAttachedParams } from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { DoesFilterPassParams } from 'ag-grid-community';
import { useGridFilterDisplay } from 'ag-grid-react';
import { Box, Select, MenuItem, FormControl, InputLabel, Divider, useMediaQuery, useTheme, TextField } from '@mui/material';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';

export enum DateFilterOperator {
    ON = 'on',
    BETWEEN = 'between',
    ON_OR_BEFORE = 'onOrBefore',
    ON_OR_AFTER = 'onOrAfter'
}

export interface DateFilterModel {
    operator: DateFilterOperator;
    date?: Date;
    fromDate?: Date;
    toDate?: Date;
}

/**
 * Date utilities that work in local timezone to avoid timezone issues
 */
const DateUtils = {
    format: (date?: Date): string => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    },

    parse: (input: string): Date | undefined => {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(input)) return undefined;
        const [year, month, day] = input.split('-').map(Number);
        return new Date(year, month - 1, day); // month is 0-indexed, creates local date
    },

    normalize: (date: Date): Date => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },

    formatInput: (value: string): string => {
        const nums = value.replace(/\D/g, '');
        if (nums.length <= 4) return nums.length === 4 ? nums + '-' : nums;
        if (nums.length <= 6) return nums.length === 6 ? `${nums.slice(0, 4)}-${nums.slice(4)}-` : `${nums.slice(0, 4)}-${nums.slice(4)}`;
        return `${nums.slice(0, 4)}-${nums.slice(4, 6)}-${nums.slice(6, 8)}`;
    }
};

/**
 * Model utilities to handle different model structures
 */
const ModelUtils = {
    getDates: (model: any) => {
        if (model.operator === DateFilterOperator.BETWEEN) {
            return {
                fromDate: model.fromDate || model.dateRange?.from,
                toDate: model.toDate || model.dateRange?.to
            };
        }
        return { date: model.date };
    },

    hasValidDates: (model: DateFilterModel): boolean => {
        if (model.operator === DateFilterOperator.BETWEEN) {
            const { fromDate, toDate } = ModelUtils.getDates(model);
            return Boolean(fromDate && toDate);
        }
        return Boolean(model.date);
    },

    createModelForOperator: (currentModel: DateFilterModel, newOperator: DateFilterOperator): DateFilterModel => {
        if (newOperator === DateFilterOperator.BETWEEN) {
            return {
                operator: newOperator,
                fromDate: currentModel.date,
                toDate: undefined
            };
        } else {
            return {
                operator: newOperator,
                date: currentModel.fromDate || currentModel.date
            };
        }
    }
};

/**
 * URL parsing utilities
 */
const URLUtils = {
    parseFromURL: (param: string): DateFilterModel | null => {
        const [operator, ...dates] = param.split('|');

        if (operator === DateFilterOperator.BETWEEN) {
            return {
                operator: operator as DateFilterOperator,
                fromDate: DateUtils.parse(dates[0] || ''),
                toDate: DateUtils.parse(dates[1] || '')
            };
        } else {
            return {
                operator: operator as DateFilterOperator,
                date: DateUtils.parse(dates[0] || '')
            };
        }
    },

    shouldLoadFromURL: (parsed: DateFilterModel): boolean => {
        return (parsed.operator === DateFilterOperator.BETWEEN && Boolean(parsed.fromDate || parsed.toDate)) ||
            (parsed.operator !== DateFilterOperator.BETWEEN && Boolean(parsed.date));
    }
};

const getDatePickerStyles = () => ({
    p: 1,
    '& .rdp': {
        '--rdp-cell-size': '22px',
        '--rdp-caption-font-size': '10px',
        '--rdp-nav-height': '22px',
        fontSize: '10px',
        margin: 0
    },
    '& .rdp-root': {
        '--rdp-cell-size': '22px',
        '--rdp-caption-font-size': '10px',
        '--rdp-nav-height': '22px',
        '--rdp-accent-color': '#1976d2',
        '--rdp-background-color': '#e3f2fd'
    },
    '& .rdp-months': {
        margin: 0,
        gap: '8px'
    },
    '& .rdp-month': {
        margin: 0
    },
    '& .rdp-table': {
        maxWidth: 'none',
        fontSize: '10px'
    },
    '& .rdp-head_cell': {
        width: '22px',
        fontSize: '9px',
        fontWeight: 500,
        minWidth: '22px'
    },
    '& .rdp-cell': {
        width: '22px',
        height: '22px',
        minWidth: '22px',
        minHeight: '22px'
    },
    '& .rdp-day': {
        width: '22px',
        height: '22px',
        fontSize: '10px',
        minWidth: '22px',
        minHeight: '22px'
    },
    '& .rdp-day_button': {
        width: '22px',
        height: '22px',
        fontSize: '10px',
        minWidth: '22px',
        minHeight: '22px',
        padding: '0'
    },
    '& .rdp-nav': {
        height: '22px'
    },
    '& .rdp-nav_button': {
        width: '18px',
        height: '18px',
        minWidth: '18px',
        minHeight: '18px'
    },
    '& .rdp-chevron': {
        width: '12px',
        height: '12px',
        fontSize: '9px'
    },
    '& .rdp-caption': {
        fontSize: '10px',
        margin: '0 0 3px 0'
    },
    '& .rdp-caption_label': {
        fontSize: '10px',
        fontWeight: '500'
    }
});

const DateFilter = ({
    filterParam,
    model,
    onModelChange
}: CustomFilterDisplayProps & {
    filterParam: string;
}) => {
    const [filterModel, setFilterModel] = useState<DateFilterModel>(
        model || { operator: DateFilterOperator.ON }
    );
    const [inputs, setInputs] = useState({ date: '', fromDate: '', toDate: '' });
    const refInput = useRef<HTMLDivElement>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
    const isRange = filterModel.operator === DateFilterOperator.BETWEEN;

    const afterGuiAttached = useCallback((params?: IAfterGuiAttachedParams) => {
        if (!params || !params.suppressFocus) {
            refInput.current?.focus();
        }
    }, []);

    useGridFilterDisplay({ afterGuiAttached });

    // Initialize model if not provided
    useEffect(() => {
        if (!model) {
            setFilterModel({ operator: DateFilterOperator.ON });
        }
    }, [model]);

    // Load from URL on mount
    useEffect(() => {
        const param = new URLSearchParams(window.location.search).get(filterParam);
        if (!param) return;

        const parsed = URLUtils.parseFromURL(param);
        if (parsed && URLUtils.shouldLoadFromURL(parsed)) {
            setFilterModel(parsed);
            onModelChange(parsed);
        }
    }, [filterParam, onModelChange]);

    // Sync inputs with model changes
    useEffect(() => {
        if (isRange) {
            setInputs(prev => ({
                ...prev,
                fromDate: DateUtils.format(filterModel.fromDate),
                toDate: DateUtils.format(filterModel.toDate),
                date: ''
            }));
        } else {
            setInputs(prev => ({
                ...prev,
                date: DateUtils.format(filterModel.date),
                fromDate: '',
                toDate: ''
            }));
        }
    }, [filterModel.operator, filterModel.date, filterModel.fromDate, filterModel.toDate, isRange]);

    const updateFilter = (updates: Partial<DateFilterModel>) => {
        let newModel = { ...filterModel, ...updates };

        // Handle operator changes with proper field mapping
        if (updates.operator && updates.operator !== filterModel.operator) {
            newModel = ModelUtils.createModelForOperator(filterModel, updates.operator);
        }

        setFilterModel(newModel);
        onModelChange(newModel); // Always pass model to AG Grid for operator tracking
    };

    const handleInput = (value: string, field: keyof typeof inputs) => {
        const formatted = DateUtils.formatInput(value);
        setInputs(prev => ({ ...prev, [field]: formatted }));

        const parsed = DateUtils.parse(formatted);
        if (field === 'date') {
            updateFilter({ date: parsed });
        } else {
            updateFilter({ [field]: parsed });
        }
    };

    const handleContainerClick = (e: React.MouseEvent) => {
        e.stopPropagation();
    };

    const renderInput = (field: keyof typeof inputs, label: string) => (
        <TextField
            size="small"
            label={label}
            placeholder="YYYY-MM-DD"
            value={inputs[field]}
            onChange={(e) => handleInput(e.target.value, field)}
            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
            fullWidth={isMobile && isRange}
            sx={{
                width: isMobile ? (isRange ? undefined : '100%') : 100,
                flex: isMobile && isRange ? 1 : undefined,
                '& .MuiInputLabel-root': { fontSize: '10px' },
                '& .MuiInputBase-input': { fontSize: '10px', padding: '8px' },
                '& .MuiInputBase-root': { height: '32px' }
            }}
        />
    );

    const renderOperatorSelect = () => (
        <FormControl
            size="small"
            sx={isMobile ? { width: '100%' } : { minWidth: 110 }}
        >
            <InputLabel sx={{ fontSize: '10px' }}>Filter type</InputLabel>
            <Select
                value={filterModel.operator}
                onChange={(e) => updateFilter({ operator: e.target.value as DateFilterOperator })}
                label="Filter type"
                sx={{
                    fontSize: '10px',
                    height: '32px',
                    '& .MuiSelect-select': { fontSize: '10px', py: 0.5 },
                    '& .MuiInputLabel-root': { fontSize: '10px' }
                }}
            >
                <MenuItem value={DateFilterOperator.ON} sx={{ fontSize: '10px' }}>On</MenuItem>
                <MenuItem value={DateFilterOperator.BETWEEN} sx={{ fontSize: '10px' }}>Between</MenuItem>
                <MenuItem value={DateFilterOperator.ON_OR_BEFORE} sx={{ fontSize: '10px' }}>On or before</MenuItem>
                <MenuItem value={DateFilterOperator.ON_OR_AFTER} sx={{ fontSize: '10px' }}>On or after</MenuItem>
            </Select>
        </FormControl>
    );

    const renderInputs = () => {
        if (isRange) {
            return isMobile ? (
                <Box sx={{ display: 'flex', gap: '2px', width: '170px' }}>
                    {renderInput('fromDate', 'From Date')}
                    {renderInput('toDate', 'To Date')}
                </Box>
            ) : (
                <>
                    {renderInput('fromDate', 'From Date')}
                    {renderInput('toDate', 'To Date')}
                </>
            );
        }

        return (
            <Box>
                {renderInput('date', 'Date')}
            </Box>
        );
    };

    const renderDatePicker = () => {
        const pickerProps = {
            navLayout: "around" as const,
            numberOfMonths: isMobile ? 1 : 2,
        };

        if (isRange) {
            return (
                <DayPicker
                    {...pickerProps}
                    mode="range"
                    selected={{ from: filterModel.fromDate, to: filterModel.toDate }}
                    onSelect={(range) => {
                        const { from, to } = range || {};

                        if (!from) {
                            return updateFilter({ fromDate: undefined, toDate: undefined });
                        }

                        // If only 'from' is selected or both dates are the same → single date selection
                        if (!to || from.getTime() === to.getTime()) {
                            return updateFilter({ fromDate: from, toDate: undefined });
                        }

                        // Otherwise → proper range selection
                        updateFilter({ fromDate: from, toDate: to });
                    }}
                />
            );
        }

        return (
            <DayPicker
                {...pickerProps}
                mode="single"
                selected={filterModel.date}
                defaultMonth={filterModel.date || new Date()}
                onSelect={(date) => {
                    updateFilter({ date });
                }}
            />
        );
    };

    return (
        <Box
            ref={refInput}
            sx={getDatePickerStyles()}
            onClick={handleContainerClick}
            onMouseDown={handleContainerClick}
            style={{ width: isMobile ? '185px' : '360px' }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'stretch' : 'center',
                gap: 1,
                mt: 1,
                mb: 1
            }}>
                {renderOperatorSelect()}
                {renderInputs()}
            </Box>

            <Divider sx={{ mb: 1 }} />
            {renderDatePicker()}
        </Box>
    );
};

DateFilter.isFilterActive = ({ model }: { model: DateFilterModel | null }) => {
    if (!model) return false;
    return ModelUtils.hasValidDates(model);
};

DateFilter.doesFilterPass = ({ model, node, handlerParams }: DoesFilterPassParams<any, any, DateFilterModel>) => {
    if (!model) return true;

    // Check if we have required dates for the operator
    if (!ModelUtils.hasValidDates(model)) return true;

    const cellValue = handlerParams.getValue(node);
    if (!cellValue) return false;

    const cellDate = new Date(cellValue);
    if (isNaN(cellDate.getTime())) return false;

    const normalizedCellDate = DateUtils.normalize(cellDate);

    switch (model.operator) {
        case DateFilterOperator.ON:
            const normalizedFilterDate = DateUtils.normalize(model.date!);
            return normalizedCellDate.getTime() === normalizedFilterDate.getTime();

        case DateFilterOperator.ON_OR_BEFORE:
            const normalizedBeforeDate = DateUtils.normalize(model.date!);
            return normalizedCellDate.getTime() <= normalizedBeforeDate.getTime();

        case DateFilterOperator.ON_OR_AFTER:
            const normalizedAfterDate = DateUtils.normalize(model.date!);
            return normalizedCellDate.getTime() >= normalizedAfterDate.getTime();

        case DateFilterOperator.BETWEEN:
            const { fromDate, toDate } = ModelUtils.getDates(model);
            const normalizedFromDate = DateUtils.normalize(fromDate!);
            const normalizedToDate = DateUtils.normalize(toDate!);

            return normalizedCellDate.getTime() >= normalizedFromDate.getTime() &&
                normalizedCellDate.getTime() <= normalizedToDate.getTime();

        default:
            return false;
    }
};

DateFilter.refresh = ({ model }: { model: DateFilterModel | null }, filterParam: string) => {
    const params = new URLSearchParams(window.location.search);

    if (!model) {
        params.delete(filterParam);
    } else if (model.operator === DateFilterOperator.BETWEEN) {
        const { fromDate, toDate } = ModelUtils.getDates(model);

        // Only include in URL if both dates are present
        if (!fromDate || !toDate) {
            params.delete(filterParam);
        } else {
            const fromDateStr = DateUtils.format(fromDate);
            const toDateStr = DateUtils.format(toDate);
            const urlValue = `${model.operator}|${fromDateStr}|${toDateStr}`;
            params.set(filterParam, urlValue);
        }
    } else {
        if (!model.date) {
            params.delete(filterParam);
        } else {
            const urlValue = `${model.operator}|${DateUtils.format(model.date)}`;
            params.set(filterParam, urlValue);
        }
    }

    const search = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${search ? '?' + search : ''}`);
};

export { DateFilter };